import { z } from 'zod';
import { ReferralConsentEvidence, ReferralConsentStatus } from '../enums';
import { dateValidation, requiredEnum } from '../../validation';

// The consents that stand behind a referral: whose consent, what for, how we
// know, and where it stands.
//
//   consent: { patient: { referral: { evidence: 'signed-form', status: 'given' } } }
//
// Today there is one: the patient's consent to BEING REFERRED. It reaches us
// three ways (see ReferralConsentEvidence) — the referrer attests to it, a
// signed form is attached, or we ask the patient ourselves by telephone — and
// those are three evidences for ONE consent, not three different consents.
// Naming the subject (`referral`) leaves room for the others without moving
// anything: the case's "share my data with the insurer and employer" consent
// would be `consent.patient.dataSharing`, and its scope has already changed
// once (PPL-2934), which is why a scope needs somewhere to live besides a
// field name.
const patientReferralConsentFields = {
  evidence: requiredEnum(
    ReferralConsentEvidence,
    'Consent evidence is required',
  ),
  // Derived from the evidence, never accepted from a caller: only a phone
  // call we have yet to make leaves a referral's consent pending
  status: requiredEnum(ReferralConsentStatus, 'Consent status is required'),
  // Set when WE record the outcome (the phone-call evidence)
  decidedAt: dateValidation().optional(),
  decidedBy: z.string().nullish(),
  // What was said about this consent, by audience — `internal` never leaves
  // Perci. A section, not one `internalComment` field, so a comment meant
  // for the partner or the member can join it without moving anything (a
  // `partnerComment` existed once and may return).
  comment: z
    .object({
      internal: z.string().max(4000).optional(),
    })
    .optional(),
};

const StoredPatientReferralConsentSchema = z.object(
  patientReferralConsentFields,
);

// As it is stored on the referral
export const ReferralConsentSchema = z
  .object({
    patient: z
      .object({
        referral: StoredPatientReferralConsentSchema.optional(),
      })
      .optional(),
  })
  .optional();

// As a referral body sends it: the evidence, and where the consent stands. Both
// are stated by the sender — we do not infer one from the other, so a partner
// who has already spoken to the member says so (`phone-call`, `given`) and one
// asking us to speak to them says that (`phone-call`, `pending`).
//
// Only the two statuses a referral can BEGIN in may be sent. `refused` and
// `unreachable` end a referral rather than start one, and are recorded through
// POST /referrals/{referralId}/consent once we have asked.
export const referralConsentCreateStatuses = [
  ReferralConsentStatus.pending,
  ReferralConsentStatus.given,
] as const;

// What we record when we ask (decidedAt, decidedBy, comment) is dropped as the
// unknown field it is.
export const ReferralConsentRequestSchema = z
  .object({
    patient: z
      .object({
        referral: z
          .object({
            evidence: patientReferralConsentFields.evidence,
            status: z.enum(referralConsentCreateStatuses, {
              error: (issue) =>
                issue.input === undefined
                  ? 'Consent status is required'
                  : `Consent status must be one of the following values: ${referralConsentCreateStatuses.join(
                      ', ',
                    )}`,
            }),
          })
          .optional(),
      })
      .optional(),
  })
  .optional();

export type ReferralConsent = NonNullable<
  z.infer<typeof ReferralConsentSchema>
>;
export type ReferralConsentRequest = NonNullable<
  z.infer<typeof ReferralConsentRequestSchema>
>;
export type ReferralPatientReferralConsent = z.infer<
  typeof StoredPatientReferralConsentSchema
>;

// What a referral's consent is when its body says nothing about it. Only for
// the callers whose evidence is settled by WHO is calling — nothing is ever
// assumed for a referral form that asks the referrer the question, or the
// answer would be filled in for them (see requiresPatientConsentEvidence).
//
// An integration or an organization's own staff referring a member IS the
// attestation: the same thing a referrer states by ticking "I confirm I have
// consent from the patient to refer them to Perci Health".
export const referrerAttestedConsent: ReferralConsentRequest = {
  patient: {
    referral: {
      evidence: ReferralConsentEvidence.referrerAttested,
      status: ReferralConsentStatus.given,
    },
  },
};

// For the organizations whose referral cannot be made without the patient's
// signed consent form attached (see requiresReferralDocumentOfType) — the
// document IS the evidence, so the schema has already enforced it.
export const signedFormConsent: ReferralConsentRequest = {
  patient: {
    referral: {
      evidence: ReferralConsentEvidence.signedForm,
      status: ReferralConsentStatus.given,
    },
  },
};

// The consent a body stated, or the endpoint's default where it stated none.
//
// `?? default` is not enough: a body can carry a `consent` section that says
// nothing — `{}`, or one whose fields were sent at the wrong depth and stripped
// — and that is not a statement. Without this, such a body stored a referral
// with NO consent at all while the default sat unused (the create schemas of
// the organizations that ASK the referrer reject it earlier; the others cannot).
export const referralConsentOrDefault = (
  consent: ReferralConsentRequest | undefined,
  fallback: ReferralConsentRequest,
): ReferralConsentRequest =>
  consent?.patient?.referral?.evidence ? consent : fallback;

// Turns what a body sent into what is stored: the evidence and the status it
// stated, and nothing else — the endpoints call this so that whatever else a
// body carried inside its consent section cannot reach the referral.
export const toStoredReferralConsent = (
  consent: ReferralConsentRequest | undefined,
): ReferralConsent | undefined => {
  const stated = consent?.patient?.referral;

  if (!stated?.evidence || !stated?.status) {
    return undefined;
  }

  return {
    patient: {
      referral: { evidence: stated.evidence, status: stated.status },
    },
  };
};

// The statuses a caller may record when we have asked the patient: 'pending'
// is not an answer, it is what a consent waits in until we do.
export const referralConsentDecisionStatuses = [
  ReferralConsentStatus.given,
  ReferralConsentStatus.refused,
  ReferralConsentStatus.unreachable,
] as const;

export type ReferralConsentDecisionStatus =
  (typeof referralConsentDecisionStatuses)[number];

// Request body of POST /referrals/:referralId/consent: the SAME consent
// section a referral body carries, filled in with how asking the patient
// ended. One shape in every body — a caller states the status it
// heard and the comment to keep, and nothing else: `pending` is not an answer,
// the evidence stands as the referral recorded it, and decidedAt/decidedBy are
// stamped by us.
export const PostReferralConsentSchema = z.object({
  // The outer levels default to `{}` so a body missing `consent` altogether
  // descends to the innermost rule and is told which field to send, rather
  // than stopping at "consent: expected object, received undefined". yup got
  // this by casting a nested object to its shape's defaults; `prefault` is
  // zod's equivalent — unlike `default` it puts the value through the schema.
  consent: z.preprocess(
    (value) => value ?? {},
    z.object({
      patient: z.preprocess(
        (value) => value ?? {},
        z.object({
          referral: z.object(
            {
              status: z.enum(
                referralConsentDecisionStatuses,
                'Consent status is required',
              ),
              comment: patientReferralConsentFields.comment,
            },
            'is required',
          ),
        }),
      ),
    }),
  ),
});

export type PostReferralConsent = z.infer<typeof PostReferralConsentSchema>;

// A consent WE still have to obtain cannot be actioned without a phone number:
// `pending` is the status of a consent waiting on a call we make. A
// create-schema rule, because the consent and the phone live at different
// object depths.
export const requiresPatientPhoneForConsent = (
  ctx: z.core.ParsePayload<any>,
) => {
  const referral = ctx.value;

  if (
    referral?.consent?.patient?.referral?.status !==
      ReferralConsentStatus.pending ||
    referral?.patient?.phone
  ) {
    return;
  }

  ctx.issues.push({
    code: 'custom',
    input: referral,
    path: ['patient', 'phone'],
    message: 'Patient telephone number is required to ask for their consent',
  });
};

// The organizations whose referral form asks the referrer to state the consent
// cannot refer without it. A create-schema rule, as the phone one is: WHICH
// evidence an organization must provide is its rule, not the shape's.
export const requiresPatientConsentEvidence = (
  ctx: z.core.ParsePayload<any>,
) => {
  const referral = ctx.value;

  if (referral?.consent?.patient?.referral?.evidence) {
    return;
  }

  ctx.issues.push({
    code: 'custom',
    input: referral,
    path: ['consent', 'patient', 'referral', 'evidence'],
    message: 'Please confirm having the consent',
  });
};
